// src/admin/pages/AdminWholesaleRequests.js
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import { AdminLanguageContext } from "../context/AdminLanguageContext";
import {
  getAdminWholesaleRequests,
  updateAdminWholesaleRequestStatus,
  deleteAdminWholesaleRequest,
} from "../../services/adminWholesaleRequests.api";
import {
  FaSearch,
  FaEye,
  FaTrash,
  FaInbox,
  FaUserShield,
} from "react-icons/fa";
import { FiCalendar } from "react-icons/fi";

function AdminWholesaleRequests() {
  const [isOpen, setIsOpen] = useState(false);
  const { lang } = useContext(AdminLanguageContext);
  const navigate = useNavigate();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  const [statusFilter, setStatusFilter] = useState("all");
  const [sortOpen, setSortOpen] = useState(false);
  const [sortBy, setSortBy] = useState(null);
  const [sortField, setSortField] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dateOverlayOpen, setDateOverlayOpen] = useState(false);
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);

  const [selectedRequests, setSelectedRequests] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  const [toast, setToast] = useState(null);

  const [page, setPage] = useState(1);
  const perPage = 10;

  const TEXT = {
    EN: {
      title: "Wholesale requests",
      search: "Search wholesale requests...",
      requester: "Requester",
      phone: "Phone",
      businessName: "Business name",
      instagramName: "Instagram name",
      instagramLink: "Instagram link",
      date: "Date",
      status: "Status",
      actions: "Actions",
      empty: "No wholesale requests found.",
      today: "Today",
      thisWeek: "This Week",
      thisMonth: "This Month",
      reset: "Reset",
      all: "All",
      new: "New",
      contacted: "Contacted",
      approved: "Approved",
      rejected: "Rejected",
      email: "Email",
      deleteTitle: "Delete wholesale request?",
      deleteDesc: "This action cannot be undone.",
      delete: "Delete",
      cancel: "Cancel",
      deleted: "Wholesale request deleted",
      statusUpdated: "Status updated",
      noAccess: "You do not have permission to access this page.",
      weekDays: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
    },
    AR: {
      title: "طلبات الجملة",
      search: "ابحث في طلبات الجملة...",
      requester: "مقدم الطلب",
      phone: "الهاتف",
      businessName: "اسم العمل",
      instagramName: "اسم الإنستغرام",
      instagramLink: "رابط الإنستغرام",
      date: "التاريخ",
      email: "البريد الإلكتروني",
      status: "الحالة",
      actions: "الإجراءات",
      empty: "لا توجد طلبات جملة.",
      today: "اليوم",
      thisWeek: "هذا الأسبوع",
      thisMonth: "هذا الشهر",
      reset: "إعادة تعيين",
      all: "الكل",
      new: "جديد",
      contacted: "تم التواصل",
      approved: "مقبول",
      rejected: "مرفوض",
      deleteTitle: "حذف طلب الجملة؟",
      deleteDesc: "لا يمكن التراجع عن هذا الإجراء.",
      delete: "حذف",
      cancel: "إلغاء",
      deleted: "تم حذف طلب الجملة",
      statusUpdated: "تم تحديث الحالة",
      noAccess: "ليس لديك صلاحية للوصول إلى هذه الصفحة.",
      weekDays: ["أحد", "اثنين", "ثلثاء", "اربعاء", "خميس", "جمعة", "سبت"],
    },
  }[lang];

  const adminUser = JSON.parse(localStorage.getItem("adminUser") || "{}");
  const perms = JSON.parse(localStorage.getItem("staffPermissions") || "{}");
  const isAdmin = adminUser.role === "admin";
  const canAccess = isAdmin || perms.can_manage_customers;

  const statusLabel = (status) => {
    const map = {
      new: TEXT.new,
      contacted: TEXT.contacted,
      approved: TEXT.approved,
      rejected: TEXT.rejected,
    };

    return map[status] || status;
  };

  const statusClass = (status) => {
    if (status === "approved") return "bg-green-50 text-green-700";
    if (status === "rejected") return "bg-red-50 text-red-700";
    if (status === "contacted") return "bg-blue-50 text-blue-700";
    return "bg-yellow-50 text-yellow-700";
  };

  const formatDate = (value) => {
    if (!value) return "—";

    return new Date(value).toLocaleDateString(lang === "AR" ? "ar-LB" : "en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (!canAccess) return;

    setLoading(true);

    getAdminWholesaleRequests()
      .then(setRequests)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [canAccess]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, dateFrom, dateTo]);

  useEffect(() => {
    const handleClick = (e) => {
      if (
        !e.target.closest(".sort-overlay") &&
        !e.target.closest(".sort-button")
      ) {
        setSortOpen(false);
      }
    };

    if (sortOpen) {
      document.addEventListener("mousedown", handleClick);
    }

    return () => document.removeEventListener("mousedown", handleClick);
  }, [sortOpen]);

  const q = search.toLowerCase();

  const filtered = requests.filter((r) => {
    const matchesSearch =
  (r.fullName || "").toLowerCase().includes(q) ||
  (r.phone || "").toLowerCase().includes(q) ||
  (r.email || "").toLowerCase().includes(q) ||
  (r.businessName || "").toLowerCase().includes(q) ||
  (r.instagramName || "").toLowerCase().includes(q) ||
  (r.instagramLink || "").toLowerCase().includes(q);

    if (!matchesSearch) return false;

    if (statusFilter !== "all" && r.status !== statusFilter) {
      return false;
    }

    if (dateFrom || dateTo) {
      const d = new Date(r.createdAt);
      if (isNaN(d.getTime())) return false;

      const createdYMD =
        d.getFullYear() +
        "-" +
        String(d.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(d.getDate()).padStart(2, "0");

      if (dateFrom && createdYMD < dateFrom) return false;
      if (dateTo && createdYMD > dateTo) return false;
    }

    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (!sortBy) return 0;

    let vA = a[sortBy] || "";
    let vB = b[sortBy] || "";

    if (sortBy === "createdAt") {
      vA = new Date(vA).getTime();
      vB = new Date(vB).getTime();
    }

    if (sortDirection === "asc") return vA > vB ? 1 : -1;
    return vA < vB ? 1 : -1;
  });

  const totalPages = Math.ceil(sorted.length / perPage);
  const currentPage = Math.min(page, totalPages || 1);

  const paginated = sorted.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  const toggleSelectAll = () => {
    if (selectedRequests.length === paginated.length) {
      setSelectedRequests([]);
    } else {
      setSelectedRequests(paginated.map((r) => r.id));
    }
  };

  const toggleRequestSelection = (id) => {
    setSelectedRequests((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const applyQuickDateFilter = (type) => {
    const today = new Date();

    const toYMD = (d) =>
      d.getFullYear() +
      "-" +
      String(d.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(d.getDate()).padStart(2, "0");

    if (type === "today") {
      const ymd = toYMD(today);
      setDateFrom(ymd);
      setDateTo(ymd);
    }

    if (type === "week") {
      const start = new Date(today);
      start.setDate(today.getDate() - 6);
      setDateFrom(toYMD(start));
      setDateTo(toYMD(today));
    }

    if (type === "month") {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateFrom(toYMD(start));
      setDateTo(toYMD(today));
    }
  };

  const resetFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setDateFrom("");
    setDateTo("");
  };

  const updateStatus = async (id, status) => {
    await updateAdminWholesaleRequestStatus(id, status);

    setRequests((prev) =>
      prev.map((r) => (r.id === id ? { ...r, status } : r))
    );

    setToast({
      type: "success",
      message: TEXT.statusUpdated,
    });

    setTimeout(() => setToast(null), 2500);
  };

  const confirmDelete = async () => {
    try {
      if (deleteId === "bulk") {
        await Promise.all(
          selectedRequests.map((id) => deleteAdminWholesaleRequest(id))
        );

        setRequests((prev) =>
          prev.filter((r) => !selectedRequests.includes(r.id))
        );

        setSelectedRequests([]);
      } else {
        await deleteAdminWholesaleRequest(deleteId);

        setRequests((prev) => prev.filter((r) => r.id !== deleteId));
      }

      setToast({
        type: "success",
        message: TEXT.deleted,
      });

      setTimeout(() => setToast(null), 2500);
    } catch (err) {
      alert(err.message);
    } finally {
      setDeleteId(null);
    }
  };

  const CalendarRangePicker = ({ rangeStart, rangeEnd, setRangeStart, setRangeEnd }) => {
    const [currentMonth, setCurrentMonth] = useState(new Date());

    const startOfMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      1
    );
    const endOfMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      0
    );

    const startDay = startOfMonth.getDay();
    const days = [];

    for (let i = 0; i < startDay; i++) days.push(null);

    for (let d = 1; d <= endOfMonth.getDate(); d++) {
      days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), d));
    }

    const isSameDay = (a, b) =>
      a &&
      b &&
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();

    const isInRange = (day) => {
      if (!rangeStart || !rangeEnd) return false;
      return day >= rangeStart && day <= rangeEnd;
    };

    const handleClick = (day) => {
      if (!rangeStart || (rangeStart && rangeEnd)) {
        setRangeStart(day);
        setRangeEnd(null);
      } else if (day < rangeStart) {
        setRangeEnd(rangeStart);
        setRangeStart(day);
      } else {
        setRangeEnd(day);
      }
    };

    return (
      <div>
        <div className="flex justify-between items-center mb-3 px-2">
          <button
            onClick={() =>
              setCurrentMonth(
                new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
              )
            }
          >
            ‹
          </button>

          <div className="font-semibold text-sm">
            {currentMonth.toLocaleString(lang === "AR" ? "ar" : "en", {
              month: "long",
              year: "numeric",
            })}
          </div>

          <button
            onClick={() =>
              setCurrentMonth(
                new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
              )
            }
          >
            ›
          </button>
        </div>

        <div className="grid grid-cols-7 text-xs text-gray-500 mb-2 px-2">
          {TEXT.weekDays.map((d) => (
            <div key={d} className="text-center">
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {days.map((day, i) => {
            if (!day) return <div key={i} />;

            const selected =
              isSameDay(day, rangeStart) || isSameDay(day, rangeEnd);
            const inRange = isInRange(day);

            return (
              <button
                key={i}
                onClick={() => handleClick(day)}
                className={`
                  h-8 rounded-md text-sm
                  ${selected ? "bg-black text-white" : ""}
                  ${inRange && !selected ? "bg-gray-200" : ""}
                  hover:bg-gray-300
                `}
              >
                {day.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const hasCustomDate = Boolean(dateFrom || dateTo);

  return (
    <div className={`flex bg-[#f1f1f1] min-h-screen ${isOpen ? "overflow-hidden" : ""}`}>
      <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

      <div className={`flex-1 ${lang === "EN" ? "lg:ml-64" : ""} w-full transition-all duration-300`}>
        <AdminHeader setIsOpen={setIsOpen} />

        <main className="p-4 sm:p-6 animate-fadeSlide">
          {!canAccess && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
              <p className="text-red-700 font-semibold">{TEXT.noAccess}</p>
            </div>
          )}

          {canAccess && (
            <>
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-6 h-6">
                    <FaUserShield className="text-gray-900 text-[18px]" />
                  </div>

                  <h1 className="text-[20px] font-semibold text-gray-900">
                    {TEXT.title}
                  </h1>
                </div>
              </div>

              {/* DESKTOP TABLE */}
              <div className="
                hidden md:block
                bg-white
                border border-[#e3e3e3]
                rounded-[14px]
                overflow-visible
                shadow-[0_12px_32px_rgba(0,0,0,0.10),0_2px_6px_rgba(0,0,0,0.06)]
              ">
                <div className="overflow-x-auto">
                  <div className="relative flex items-center justify-between px-4 py-3 border-b border-[#e3e3e3] bg-white rounded-t-[14px]">

                    {sortOpen && (
                      <div
                        className="sort-overlay fixed w-56 bg-white border border-gray-200 rounded-xl shadow-[0_30px_30px_rgba(0,0,0,0.15)] z-[9999] animate-sortOverlay"
                        style={{
                          top: "120px",
                          ...(lang === "AR" ? { left: "40px" } : { right: "40px" }),
                        }}
                      >
                        <div className="px-4 pt-4 text-xs font-semibold uppercase tracking-wide">
                          {lang === "AR" ? "ترتيب حسب" : "Sort by"}
                        </div>

                        <div className="mt-1 text-sm">
                          {[
                            { label: TEXT.requester, value: "fullName" },
                            { label: TEXT.businessName, value: "businessName" },
                            { label: TEXT.status, value: "status" },
                            { label: TEXT.date, value: "createdAt" },
                          ].map((opt) => (
                            <label
                              key={opt.value}
                              className={`flex items-center gap-3 px-3 py-2 cursor-pointer rounded-md hover:bg-gray-100 ${
                                sortField === opt.value ? "bg-gray-100 font-medium" : ""
                              }`}
                            >
                              <input
                                type="radio"
                                checked={sortField === opt.value}
                                onChange={() => setSortField(opt.value)}
                                className="w-4 h-4 accent-gray-800"
                              />
                              {opt.label}
                            </label>
                          ))}
                        </div>

                        <div className="border-t my-2"></div>

                        <button
                          onClick={() => {
                            setSortBy(sortField);
                            setSortDirection("asc");
                            setSortOpen(false);
                          }}
                          className="px-3 py-2 text-sm w-full text-left hover:bg-gray-100"
                        >
                          ↑ {lang === "AR" ? "تصاعدي" : "Ascending"}
                        </button>

                        <button
                          onClick={() => {
                            setSortBy(sortField);
                            setSortDirection("desc");
                            setSortOpen(false);
                          }}
                          className="px-3 py-2 text-sm w-full text-left hover:bg-gray-100"
                        >
                          ↓ {lang === "AR" ? "تنازلي" : "Descending"}
                        </button>

                        {sortField === "createdAt" && (
                          hasCustomDate ? (
                            <button
                              onClick={() => {
                                setDateFrom("");
                                setDateTo("");
                                setDateOverlayOpen(false);
                                setSortOpen(false);
                              }}
                              className="px-3 py-2 text-sm w-full text-left hover:bg-gray-100"
                            >
                              ↺ {lang === "AR" ? "إعادة تعيين التاريخ" : "Reset date"}
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                setDateOverlayOpen(true);
                                setSortOpen(false);
                              }}
                              className="px-3 py-2 text-sm w-full text-left hover:bg-gray-100"
                            >
                              <div className="flex items-center gap-2">
                                <FiCalendar className="text-gray-600 text-[14px]" />
                                {lang === "AR" ? "تاريخ مخصص" : "Custom date"}
                              </div>
                            </button>
                          )
                        )}
                      </div>
                    )}

                    <div className="flex items-center gap-2">
                      {["all", "new", "contacted", "approved", "rejected"].map((s) => (
                        <button
                          key={s}
                          onClick={() => setStatusFilter(s)}
                          className={`px-3 py-1 text-xs rounded-md transition ${
                            statusFilter === s
                              ? "bg-gray-900 text-white"
                              : "bg-gray-100 text-gray-900"
                          }`}
                        >
                          {s === "all" ? TEXT.all : statusLabel(s)}
                        </button>
                      ))}
                    </div>

                    <div className="flex items-center gap-2">
                      {selectedRequests.length > 0 && (
                        <button
                          onClick={() => setDeleteId("bulk")}
                          className="
                            flex items-center justify-center
                            w-9 h-9
                            bg-white
                            border border-gray-300
                            rounded-lg
                            shadow-sm
                            hover:bg-gray-50
                            transition
                          "
                        >
                          <FaTrash className="text-gray-600 text-sm" />
                        </button>
                      )}

                      {!searchOpen ? (
                        <button
                          onClick={() => setSearchOpen(true)}
                          className="
                            flex items-center justify-center gap-1
                            w-9 h-9
                            bg-white border border-gray-300 rounded-lg
                            shadow-sm hover:bg-gray-50 transition
                          "
                        >
                          <FaSearch className="text-gray-600 text-sm" />
                        </button>
                      ) : (
                        <div className="flex items-center gap-3">
                          <div className="flex items-center h-9 px-3 bg-white border border-gray-300 rounded-lg shadow-sm">
                            <FaSearch className="text-gray-400 text-sm" />

                            <input
                              autoFocus
                              value={search}
                              onChange={(e) => setSearch(e.target.value)}
                              placeholder={TEXT.search}
                              className="ml-2 outline-none text-sm"
                            />

                            <button
                              onClick={() => {
                                setSearch("");
                                setSearchOpen(false);
                              }}
                              className="ml-2 text-gray-400"
                            >
                              ✕
                            </button>
                          </div>

                          <button
                            onClick={() => {
                              setSearch("");
                              setSearchOpen(false);
                            }}
                            className="
                              h-9 px-4
                              bg-white border border-gray-300
                              rounded-lg
                              text-sm
                              hover:bg-gray-50
                            "
                          >
                            {lang === "AR" ? "إلغاء" : "Cancel"}
                          </button>
                        </div>
                      )}

                      <button
                        onClick={() => setSortOpen((v) => !v)}
                        className="
                          sort-button
                          flex items-center justify-center
                          w-9 h-9 text-gray-600
                          bg-white border border-gray-300 rounded-lg
                          shadow-sm hover:bg-gray-50 transition
                        "
                      >
                        ⇅
                      </button>
                    </div>
                  </div>

                  <table
                    dir={lang === "AR" ? "rtl" : "ltr"}
                    className="min-w-[900px] w-full"
                  >
                    <thead className="bg-[#f6f6f7] text-[#6d7175] text-[13px] font-medium">
                      <tr>
                        <th className="p-3 text-center">
                          <input
                            type="checkbox"
                            checked={
                              selectedRequests.length === paginated.length &&
                              paginated.length > 0
                            }
                            onChange={toggleSelectAll}
                            className="w-4 h-4 accent-gray-800 cursor-pointer"
                          />
                        </th>
                        <th className="p-3 text-center">{TEXT.requester}</th>
                        <th className="p-3 text-center">{TEXT.phone}</th>
                        <th className="p-3 text-center">{TEXT.email}</th>
                        <th className="p-3 text-center">{TEXT.businessName}</th>
                        <th className="p-3 text-center">{TEXT.instagramName}</th>
                        <th className="p-3 text-center">{TEXT.date}</th>
                        <th className="p-3 text-center">{TEXT.status}</th>
                        <th className="p-3 text-center">{TEXT.actions}</th>
                      </tr>
                    </thead>

                    <tbody>
                      {loading ? (
                        [...Array(6)].map((_, i) => (
                          <tr key={i} className="animate-pulse border-b">
                            {[...Array(9)].map((_, j) => (
                              <td key={j} className="p-3">
                                <div className="h-3 bg-gray-200 rounded w-3/4 mx-auto"></div>
                              </td>
                            ))}
                          </tr>
                        ))
                      ) : filtered.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="py-12 text-center">
                            <div className="flex flex-col items-center gap-2 text-gray-500">
                              <FaInbox className="text-2xl opacity-60" />
                              <p className="text-sm font-medium">{TEXT.empty}</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        paginated.map((r) => (
                          <tr
                            key={r.id}
                            onClick={() => navigate(`/admin/wholesale-requests/${r.id}`)}
                            className="
                              group
                              border-b border-[#f1f1f1]
                              hover:bg-[#f6f6f7]
                              transition
                              text-[13px]
                              cursor-pointer
                            "
                          >
                            <td
                              className="p-3 text-center"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={selectedRequests.includes(r.id)}
                                onChange={() => toggleRequestSelection(r.id)}
                                className="w-4 h-4 accent-gray-800 cursor-pointer"
                              />
                            </td>

                            <td className="py-1 px-3 font-semibold text-center">
                              {r.fullName}
                            </td>

                            <td className="py-1 px-3 text-center">
                              {r.phone || "—"}
                            </td>
<td className="py-1 px-3 text-center">
  {r.email ? (
    r.email
  ) : (
    <span className="inline-flex items-center px-2 py-[2px] rounded-md text-[11px] font-medium bg-gray-100 text-gray-600">
      —
    </span>
  )}
</td>
                            <td className="py-1 px-3 text-center">
                              {r.businessName || "—"}
                            </td>

                            <td className="py-1 px-3 text-center">
                              {r.instagramName || "—"}
                            </td>

                            <td className="py-1 px-3 text-center">
                              {formatDate(r.createdAt)}
                            </td>

                            <td className="py-1 px-3 text-center">
                              <select
                                value={r.status}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => updateStatus(r.id, e.target.value)}
                                className={`px-3 py-1 rounded-full text-sm font-semibold border-0 outline-none ${statusClass(r.status)}`}
                              >
                                <option value="new">{TEXT.new}</option>
                                <option value="contacted">{TEXT.contacted}</option>
                                <option value="approved">{TEXT.approved}</option>
                                <option value="rejected">{TEXT.rejected}</option>
                              </select>
                            </td>

                            <td className="py-1 px-3 text-center">
                              <div className="flex items-center justify-center gap-3">
                                <button
                                  className="w-8 h-8 flex items-center justify-center rounded-md bg-white border border-gray-300 shadow-sm hover:bg-gray-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/admin/wholesale-requests/${r.id}`);
                                  }}
                                >
                                  <FaEye />
                                </button>

                                <button
                                  className="w-8 h-8 flex items-center justify-center rounded-md bg-white border border-gray-300 shadow-sm hover:bg-gray-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteId(r.id);
                                  }}
                                >
                                  <FaTrash className="text-red-600 text-[13px]" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>

                    <tfoot>
                      <tr className="bg-[#f6f6f7] border-t border-[#e3e3e3]">
                        <td colSpan="9" className="px-4 py-2 rounded-b-[14px]">
                          <div className="flex items-center justify-between text-sm text-gray-600">
                            <span className="text-gray-500 text-sm">
                              {filtered.length} {TEXT.title}
                            </span>

                            <div className="flex items-center gap-0.5">
                              <button
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className={`w-7 h-7 flex font-bold items-center justify-center rounded-md border bg-gray-200 ${
                                  currentPage === 1
                                    ? "opacity-40 cursor-not-allowed"
                                    : "hover:bg-gray-300"
                                }`}
                              >
                                {"<"}
                              </button>

                              <button
                                onClick={() =>
                                  setPage((p) => Math.min(totalPages || 1, p + 1))
                                }
                                disabled={currentPage === totalPages || totalPages === 0}
                                className={`w-7 h-7 flex items-center font-bold justify-center rounded-md border bg-gray-200 ${
                                  currentPage === totalPages || totalPages === 0
                                    ? "opacity-40 cursor-not-allowed"
                                    : "hover:bg-gray-300"
                                }`}
                              >
                                {">"}
                              </button>

                              <span className="mx-2 text-gray-600 font-bold">
                                {currentPage}-{totalPages || 1}
                              </span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* MOBILE VIEW */}
              <div className="md:hidden">
                <div className="bg-white border border-gray-200 rounded-xl p-3 mb-3 shadow-sm space-y-3">
                  <div className="flex items-center gap-2 border rounded-lg px-3 h-10">
                    <FaSearch className="text-gray-400 text-sm" />
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder={TEXT.search}
                      className="flex-1 outline-none text-sm bg-transparent"
                    />
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    {["all", "new", "contacted", "approved", "rejected"].map((s) => (
                      <button
                        key={s}
                        onClick={() => setStatusFilter(s)}
                        className={`px-3 py-1 text-xs rounded-md transition ${
                          statusFilter === s
                            ? "bg-gray-900 text-white"
                            : "bg-gray-100 text-gray-900"
                        }`}
                      >
                        {s === "all" ? TEXT.all : statusLabel(s)}
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => applyQuickDateFilter("today")}
                      className="px-3 py-1 text-xs rounded-md bg-gray-100 hover:bg-gray-200"
                    >
                      {TEXT.today}
                    </button>

                    <button
                      onClick={() => applyQuickDateFilter("week")}
                      className="px-3 py-1 text-xs rounded-md bg-gray-100 hover:bg-gray-200"
                    >
                      {TEXT.thisWeek}
                    </button>

                    <button
                      onClick={() => applyQuickDateFilter("month")}
                      className="px-3 py-1 text-xs rounded-md bg-gray-100 hover:bg-gray-200"
                    >
                      {TEXT.thisMonth}
                    </button>

                    <button
                      onClick={resetFilters}
                      className="px-3 py-1 text-xs rounded-md bg-red-50 text-red-600"
                    >
                      {TEXT.reset}
                    </button>
                  </div>

                  <div className="text-xs text-gray-500">
                    {filtered.length} {TEXT.title}
                  </div>
                </div>

                {loading ? (
                  [...Array(4)].map((_, i) => (
                    <div
                      key={i}
                      className="bg-white p-4 rounded-xl shadow animate-pulse space-y-3 mb-3"
                    >
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                      <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                    </div>
                  ))
                ) : filtered.length === 0 ? (
                  <div className="bg-white p-6 rounded-xl shadow text-center">
                    <div className="flex flex-col items-center gap-2 text-gray-500">
                      <FaInbox className="text-2xl opacity-60" />
                      <p className="text-sm font-medium">{TEXT.empty}</p>
                    </div>
                  </div>
                ) : (
                  paginated.map((r) => (
                    <div
                      key={r.id}
                      onClick={() => navigate(`/admin/wholesale-requests/${r.id}`)}
                      className="
                        bg-white
                        border border-[#e3e3e3]
                        rounded-[14px]
                        shadow-[0_8px_20px_rgba(0,0,0,0.08)]
                        p-4
                        space-y-3
                        mb-3
                        active:scale-[0.99]
                        transition
                        cursor-pointer
                      "
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-11 h-11 rounded-full bg-gray-200 flex items-center justify-center text-gray-900 font-semibold uppercase">
                            {(r.fullName || "W").charAt(0)}
                          </div>

                          <div>
                            <div className="font-semibold text-gray-900">
                              {r.fullName}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              {r.businessName || "—"}
                            </div>
                          </div>
                        </div>

                        <span
                          className={`inline-flex items-center justify-center px-2.5 py-[2px] rounded-md text-[11px] font-medium ${statusClass(r.status)}`}
                        >
                          {statusLabel(r.status)}
                        </span>
                      </div>

                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">{TEXT.phone}</span>
                        <span className="text-gray-900">{r.phone || "—"}</span>
                      </div>
<div className="flex items-center justify-between text-sm">
  <span className="text-gray-500">{TEXT.email}</span>
  <span className="text-gray-900">{r.email || "—"}</span>
</div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">{TEXT.instagramName}</span>
                        <span className="text-gray-900">{r.instagramName || "—"}</span>
                      </div>

                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">{TEXT.date}</span>
                        <span className="text-gray-900">{formatDate(r.createdAt)}</span>
                      </div>

                      <div className="flex items-center justify-end gap-2 pt-2 border-t">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteId(r.id);
                          }}
                          className="
                            w-9 h-9 flex items-center justify-center
                            rounded-md
                            bg-white border border-gray-300
                            shadow-sm hover:bg-gray-50 transition
                          "
                        >
                          <FaTrash className="text-red-600 text-[13px]" />
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/wholesale-requests/${r.id}`);
                          }}
                          className="
                            w-9 h-9 flex items-center justify-center
                            rounded-md
                            bg-white border border-gray-300
                            shadow-sm hover:bg-gray-50 transition
                          "
                        >
                          <FaEye className="text-gray-900 text-[13px]" />
                        </button>
                      </div>
                    </div>
                  ))
                )}

                {!loading && filtered.length > 0 && (
                  <div className="sticky bottom-0 bg-white border-t p-3 flex items-center justify-between text-sm">
                    <span className="text-xs text-gray-500">
                      {currentPage} / {totalPages || 1}
                    </span>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1.5 rounded-md border bg-white disabled:opacity-40"
                      >
                        {lang === "AR" ? "السابق" : "Prev"}
                      </button>

                      <button
                        onClick={() => setPage((p) => Math.min(totalPages || 1, p + 1))}
                        disabled={currentPage === totalPages || totalPages === 0}
                        className="px-3 py-1.5 rounded-md border bg-white disabled:opacity-40"
                      >
                        {lang === "AR" ? "التالي" : "Next"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </main>
      </div>

      {dateOverlayOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="w-[360px] bg-white border border-gray-200 rounded-[18px] shadow-[0_30px_60px_rgba(0,0,0,0.16)] overflow-hidden animate-sortOverlay">
            <div className="px-5 py-4 bg-[#f6f6f7] border-b border-gray-200">
              <h3 className="text-[15px] font-semibold text-gray-800">
                {lang === "AR" ? "نطاق تاريخ" : "Date range"}
              </h3>
            </div>

            <div className="p-4">
              <CalendarRangePicker
                rangeStart={rangeStart}
                rangeEnd={rangeEnd}
                setRangeStart={setRangeStart}
                setRangeEnd={setRangeEnd}
              />
            </div>

            <div className="px-5 py-4 bg-[#f6f6f7] border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setDateOverlayOpen(false)}
                className="h-[36px] px-4 border rounded-lg bg-white"
              >
                {lang === "AR" ? "إلغاء" : "Cancel"}
              </button>

              <button
                onClick={() => {
                  if (!rangeStart || !rangeEnd) return;

                  setDateFrom(formatLocalDate(rangeStart));
                  setDateTo(formatLocalDate(rangeEnd));
                  setDateOverlayOpen(false);
                }}
                className="h-[36px] px-4 bg-[#202223] text-white rounded-lg"
              >
                {lang === "AR" ? "تطبيق" : "Apply"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-[380px] bg-white rounded-[14px] shadow-xl overflow-hidden">
            <div className="px-5 py-4 border-b bg-[#f6f6f7] flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-red-50 border">
                <FaTrash className="text-red-600 text-sm" />
              </div>

              <h2 className="text-[15px] font-semibold">
                {TEXT.deleteTitle}
              </h2>
            </div>

            <div className="px-5 py-5 text-sm text-gray-600">
              {TEXT.deleteDesc}
            </div>

            <div className="px-5 py-4 border-t flex justify-end gap-3 bg-[#f6f6f7]">
              <button
                onClick={() => setDeleteId(null)}
                className="h-[36px] px-4 border rounded-lg bg-white"
              >
                {TEXT.cancel}
              </button>

              <button
                onClick={confirmDelete}
                className="h-[36px] px-4 bg-red-600 text-white rounded-lg"
              >
                {TEXT.delete}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-5 right-5 z-[9999] animate-fadeSlide">
          <div className="
            flex items-center gap-3
            px-4 py-3
            bg-[#202223]
            text-white
            rounded-lg
            shadow-[0_10px_25px_rgba(0,0,0,0.2)]
            text-sm font-medium
            min-w-[220px]
          ">
            <div className="w-5 h-5 flex items-center justify-center rounded-full bg-white/20 text-xs">
              ✓
            </div>
            <span>{toast.message}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminWholesaleRequests;